import { conditionsWind, REALISM, realismOptions, type AirportData } from '@atc/core';
import { createSim, type Sim, type SimOptions } from '@atc/sim';
import { LESSON_SIM_OPTIONS, type Lesson } from './academy/lesson';
import { TRAFFIC, type SessionSettings } from './settings';
import { scenarioProgress, setupScenario, type ScenarioId, type ScenarioProgress } from './scenarios';

/** One source for mode overrides. Scripted training never inherits random traffic. */
export function createSessionSim(airport: AirportData, settings: SessionSettings, lesson?: Lesson): Sim {
  // A lesson runs at the player's realism, except for the flags its checks depend on: wake
  // separation is off at low realism, so a wake lesson would be unpassable for a player who set it.
  const lessonRealism = lesson?.realismOverrides
    ? { ...realismOptions(settings.realism), ...lesson.realismOverrides }
    : settings.realism;
  const sessionOptions: SimOptions = lesson
    ? { realism: lessonRealism, shortCallsigns: settings.shortCallsigns, seed: 1, academyApproach: lesson.position === 'approach', ...(lesson.runways ? { activeRunways: lesson.runways } : {}), ...LESSON_SIM_OPTIONS }
    : settings.mode === 'scenario'
      ? { realism: { ...REALISM.mid, wind: 'none', complianceDelayS: [0, 0], runwayOccupancyCheck: true }, seed: 7, ...LESSON_SIM_OPTIONS }
      : { realism: settings.realism, ...sessionWind(airport, settings), shortCallsigns: settings.shortCallsigns, seed: settings.seed ?? Date.now() % 100000, trafficRamp: true, emergencies: settings.emergencies, activeRunways: { arrivals: settings.arrivalRunways, departures: settings.departureRunways }, ...TRAFFIC[settings.traffic] };
  const tower = (lesson ? lesson.position === 'tower' : settings.position === 'tower') && airport.icao.toLowerCase() === 'ksfo';
  const sim = createSim(airport, { ...sessionOptions, position: tower ? 'tower' : 'legacy', coaching: settings.coaching !== false });
  sim.configurePilots({ initiative: settings.pilotInitiative, shortCallsigns: settings.shortCallsigns });
  return sim;
}

/** The prevailing wind the chosen weather gives a free-play or shift session. */
export function sessionWind(airport: AirportData, settings: Pick<SessionSettings, 'weather'>): { wind: { directionDeg: number; speedKt: number }; gustyWind: boolean } {
  const w = conditionsWind(airport, settings.weather);
  return { wind: { directionDeg: w.directionDeg, speedKt: w.speedKt }, gustyWind: w.gusty };
}

/** Counts objectives once and latches the terminal state independently of the UI. */
export function startChallenge(sim: Sim, id: ScenarioId) {
  let departures = 0;
  let terminal: ScenarioProgress | null = null;
  const off = sim.on('event', (e) => { if (e.type === 'airborne') departures++; });
  setupScenario(sim, sim.airport, id);
  return { update(): ScenarioProgress { const p = terminal ?? scenarioProgress(sim, id, departures); if (p.status !== 'running') terminal = p; return p; }, dispose: off };
}
